import React, { useEffect, useRef, useContext, useCallback } from "react";
import {
  MlVectorTileLayer,
  useMap,
} from "@mapcomponents/react-maplibre";
import { useState } from "react";
import Sidebar from "./Sidebar";
import BottomSidebar from "./BottomSidebar";
import FeatureInfo from "./FeatureInfo";
import useLayerEvent from "../hooks/useLayerEvent";
import useWindowSize from "../hooks/useWindowSize";
import InfoPopper from "./infoPoper";
import config from "../config";
import { DataContext } from "../context/DataContext";
import { SpinnerCircularFixed } from "spinners-react";
import { SearchContext } from "../context/SearchContext";
import { useTheme } from "@mui/material/styles";
import {defaultTheme} from "../index";


const HOVER_QUERY_LAYERS = [
  "flik-data_sachs",
  "flik-data_lines",
  "flik-data_lines_center",
];

export default function DataLayer() {
  const mapHook = useMap({ mapId: "map_1" });
  const dataContext = useContext(DataContext);
  const searchContext = useContext(SearchContext);
  const [hoveredFeature, setHoveredFeature] = useState();
  const hoveredStateId = useRef(undefined);
  const theme = useTheme(defaultTheme);
  const size = useWindowSize();

  const clearHoveredFeature = useCallback(() => {
    if (!mapHook.map || !hoveredStateId.current) {
      hoveredStateId.current = undefined;
      setHoveredFeature(undefined);
      return;
    }

    mapHook.map.setFeatureState(
      {
        sourceLayer: "sql_statement",
        source: "flik-data",
        id: hoveredStateId.current,
      },
      { hover: false }
    );
    hoveredStateId.current = undefined;
    setHoveredFeature(undefined);
  }, [mapHook.map]);

  useLayerEvent({
    event: "click",
    layerId: "flik-data_sachs",
    condition: !dataContext.flikDataHidden,
    eventHandler: (ev) => {
       
      if (ev.originalEvent.ctrlKey && ev?.features?.[0]) {
        let featureState = mapHook.map.getFeatureState(ev.features[0]);
        if (featureState?.selected === true) {
          let newSelectedFeatures = [];
          dataContext.setSelectedFeatures((current) => {
            newSelectedFeatures = current.filter(
              (el) => el.id !== ev.features[0].id
            );
            return newSelectedFeatures;
          });
          mapHook.map.setFeatureState(
            {
              sourceLayer: "sql_statement",
              source: "flik-data",
              id: ev.features[0].id,
            },
            { selected: false }
          );

          if (
            dataContext.selectedFromList === ev.features[0].id &&
            newSelectedFeatures.length
          ) {
            dataContext.setSelectedFromList(newSelectedFeatures[0].id);
            mapHook.map.setFeatureState(
              {
                sourceLayer: "sql_statement",
                source: "flik-data",
                id: newSelectedFeatures[0].id,
              },
              { selected: true }
            );
          }
        } else {
          dataContext.setSelectedFeatures((current) => {
            return [...current, ev.features?.[0]];
          });

          mapHook.map.setFeatureState(
            {
              sourceLayer: "sql_statement",
              source: "flik-data",
              id: ev.features[0].id,
            },
            { selected: true }
          );
          dataContext.setSelectedFromList(ev.features[0].id);
        }
      } else {
        dataContext.selectedFeatures.forEach((element) => {
          mapHook.map.setFeatureState(
            {
              sourceLayer: "sql_statement",
              source: "flik-data",
              id: element.id,
            },
            { selected: false, fromListSelected: false }
          );
        });
        dataContext.setSelectedFeatures([ev.features[0]]);
        dataContext.setSelectedFromList(ev.features[0].id);
        mapHook.map.setFeatureState(
          {
            sourceLayer: "sql_statement",
            source: "flik-data",
            id: ev.features[0].id,
          },
          { selected: true }
        );
      }
    },
  });

  useEffect(() => {
    if (!mapHook.map) return;

    dataContext.selectedFeatures.forEach((element) => {
      mapHook.map.setFeatureState(
        {
          sourceLayer: "sql_statement",
          source: "flik-data",
          id: element.id,
        },
        { fromListSelected: false }
      );
    });
    mapHook.map.setFeatureState(
      {
        sourceLayer: "sql_statement",
        source: "flik-data",
        id: dataContext.selectedFromList,
      },
      { fromListSelected: true }
    );
    // eslint-disable-next-line
  }, [dataContext.selectedFromList, mapHook.map]);

  useLayerEvent({
    event: "mousemove",
    layerId: "flik-data_sachs",
    condition: !dataContext.flikDataHidden,
    eventHandler: function (e) {
      if (e.features[0].id) {
        setHoveredFeature(e.features[0]);
        if (
          hoveredStateId.current &&
          e.features[0].id !== hoveredStateId.current
        ) {
          mapHook.map.setFeatureState(
            {
              sourceLayer: "sql_statement",
              source: "flik-data",
              id: hoveredStateId.current,
            },
            { hover: false }
          );
        }
        hoveredStateId.current = e.features[0].id;

        mapHook.map.setFeatureState(
          {
            sourceLayer: "sql_statement",
            source: "flik-data",
            id: hoveredStateId.current,
          },
          { hover: true }
        );
      }
    },
  });

  useLayerEvent({
    event: "mouseleave",
    layerId: "flik-data_sachs",
    condition: !dataContext.flikDataHidden,
    eventHandler: function (e) {
      clearHoveredFeature();
    },
  });

useEffect(() => {
  if (!mapHook.map || dataContext.flikDataHidden) return;

  const handleMouseMove = (event) => {
    if (!hoveredStateId.current) return;
    if (!mapHook.map.isStyleLoaded()) return;

    const existingLayers = HOVER_QUERY_LAYERS.filter((layerId) =>
      mapHook.map.getLayer(layerId)
    );

    // If the layer set is not available right now, clear hover safely.
    if (existingLayers.length === 0) {
      clearHoveredFeature();
      return;
    }

    const hoveredFeatures = mapHook.map.queryRenderedFeatures(event.point, {
      layers: existingLayers,
    });

    if (!hoveredFeatures.length) {
      clearHoveredFeature();
    }
  };

  mapHook.map.on("mousemove", handleMouseMove);
  return () => mapHook.map.off("mousemove", handleMouseMove);
}, [clearHoveredFeature, dataContext.flikDataHidden, mapHook.map]);

  

  const [headerExtended, setHeaderExtended] = useState(true);

  useEffect(() => {
    setHeaderExtended(true);
  }, [dataContext.selectedFeatures]);

  useEffect(() => {
    if (searchContext.searchResult) {
      dataContext.selectedFeatures.forEach((element) => {
        mapHook.map.setFeatureState(
          {
            sourceLayer: "sql_statement",
            source: "flik-data",
            id: element.id,
          },
          { selected: false, fromListSelected: false }
        );
      });
      const elementsToAdd = [];
      searchContext.searchResult.forEach((element) => {
        if (element.properties.feldblockidentnummer) {
          elementsToAdd.push(element);
          mapHook.map.setFeatureState(
            {
              sourceLayer: "sql_statement",
              source: "flik-data",
              id: element.id,
            },
            { selected: true }
          );
        }
      });
      dataContext.setSelectedFeatures(elementsToAdd);
    }
    // eslint-disable-next-line
  }, [searchContext.searchResult]);

  return (
    <>
      {dataContext.loading && (
        <div
          style={{
            display: "flex",
            alignContent: "center",
            justifyContent: "center",
            position: "fixed",
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 2000,
            backgroundColor: "rgba(0,0,0,0.5)",
          }}
        >
          {" "}
          <SpinnerCircularFixed secondaryColor="#fff" color="#2071b5" />
        </div>
      )}
      <MlVectorTileLayer
        mapId="map_1"
        layerId="flik-data"
        url={
          config.tileServerHost + "tileservergl/data/flik_brd/{z}/{x}/{y}.pbf?"
        }
        insertBeforeLayer="marker-data"
        sourceOptions={{ maxzoom: 16, minzoom: 8, promoteId: "gid" }}
        layers={[
          {
            id: 'flik-data_sachs',
            type: "fill",
            "source-layer": "sql_statement",
            paint: {
              "fill-color": [
                "case",
                ["==", ["get", "status"], "geeignet"],
                theme.palette.layers?.apt,
                ["==", ["get", "status"], "ungeeignet"],
                theme.palette.layers?.not_apt,
                ["==", ["get", "status"], "unklar"],
                theme.palette.layers?.not_defined,
                theme.palette.layers?.not_defined
              ],

              "fill-opacity": dataContext.flikDataHidden || !dataContext.showFlikDataFill
                ? 0
                : [
                    "case",
                    ["boolean", ["feature-state", "hover"], false],
                    0.1,

                    [
                      "case",
                      ["boolean", ["feature-state", "selected"], false],
                      0,
                      dataContext.flikDataOpacity,
                    ],
                  ],
                "fill-outline-color" : [
                "case",
                ["==", ["get", "status"], "geeignet"],
                theme.palette.layers?.apt,
                ["==", ["get", "status"], "ungeeignet"],
                theme.palette.layers?.not_apt,
                ["==", ["get", "status"], "unklar"],
                theme.palette.layers?.not_defined,
                ["==", ["get", "status"], "unbekannt"],
                theme.palette.layers?.not_defined,
                theme.palette.layers?.not_defined
              ]
            },
            maxzoom: 22,
            minzoom: 8,
          },
          {
            id: 'flik-data_lines',
            type: "line",
            "source-layer": "sql_statement",
            paint: {
              "line-width": [
                "case",
                ["==", ["feature-state", "selected"], true],
                [
                  "case",
                  ["==", ["feature-state", "fromListSelected"], true],
                  5,
                  3,
                ],
                1,
              ],
              "line-opacity": dataContext.flikDataHidden
                ? 0
                : ["interpolate", ["exponential", 1], ["zoom"], 8, 0, 14, 1],
              "line-color": [
                "case",
                ["boolean", ["feature-state", "selected"], false],
                [
                  "case",
                  ["boolean", ["feature-state", "fromListSelected"], false],
                  theme.palette.layers?.line_list_selected,
                  theme.palette.layers?.line_list_unselected,
                ],
                [
                  "match",
                  ["get", "status"],
                  "geeignet",
                  theme.palette.layers?.apt,
                  "ungeeignet",
                  theme.palette.layers?.not_apt,
                  "unklar",
                  theme.palette.layers?.not_defined,
                  theme.palette.layers?.not_defined,
                ],
            ],
            },
            maxzoom: 22,
            minzoom: 8,
          },
          {
            id: 'flik-data_lines_center',
            type: "line",
            "source-layer": "sql_statement",
            paint: {
              "line-width": [
                "case",
                ["==", ["feature-state", "selected"], true],
                1,
                "case",
                ["==", ["feature-state", "selected"], false],
                0,
                ],

                "line-color":    "#000000"

            },
            maxzoom: 22,
            minzoom: 8,
          }
        ]}
      />

      {size.width > 980 && !dataContext.flikDataHidden && (
        <InfoPopper feature={hoveredFeature} />
      )}

      {dataContext.selectedFeatures[0] && !dataContext.flikDataHidden && (
        <>
          {size.width > 600 && (
            <>
              <Sidebar
                extended={headerExtended}
                setHeaderExtended={setHeaderExtended}
              >
                <FeatureInfo feature={dataContext.selectedFeatures} isMobil={false}/>
              </Sidebar>
            </>
          )}
          {size.width < 600 && (
            <>
              <BottomSidebar>
                <FeatureInfo feature={dataContext.selectedFeatures} isMobil={true} />
              </BottomSidebar>
            </>
          )}
        </>
      )}
    </>
  );
}
